// File de décision : tout ce qui attend l'acte d'une autorité dans le contexte actif, construit à
// partir des routes réelles (dossiers en attente de décision ou en signature, courriers départ
// validés à signer). Les éléments en retard passent en tête.
import type { Courrier, Dossier, LiquidationDepense } from '@cc/contrats';
import type { ListePaginee } from '@cc/contrats/ui-metier';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { requete } from '../../api/client';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';

// mandat_a_ordonnancer (lot L4-B) : liquidation validée qui attend le mandat du maire ou de son délégué.
export type TypeElement = 'dossier_decision' | 'dossier_signature' | 'courrier_signature' | 'mandat_a_ordonnancer';

export interface ElementFile {
  id: string;
  type: TypeElement;
  reference: string;
  titre: string;
  sousTitre: string;
  enRetard: boolean;
  echeance: string | null;
  /** Clé de tri : retard d'abord, puis échéance la plus proche. */
  ordre: number;
}

const CLE_FILE = ['decisions', 'file'] as const;

function ordre(enRetard: boolean, echeance: string | null): number {
  const t = echeance ? Date.parse(echeance) : Number.MAX_SAFE_INTEGER;
  return (enRetard ? 0 : 1e15) + (Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER);
}

function depuisDossier(d: Dossier, type: TypeElement): ElementFile {
  return {
    id: d.id,
    type,
    reference: d.numero ?? 'Sans numéro',
    titre: d.demarche.libelle,
    sousTitre: [d.demandeur.nom, d.service?.nom].filter(Boolean).join(' · '),
    enRetard: Boolean(d.en_retard),
    echeance: d.echeance_interne ?? null,
    ordre: ordre(Boolean(d.en_retard), d.echeance_interne ?? null),
  };
}

function depuisCourrier(c: Courrier): ElementFile {
  return {
    id: c.id,
    type: 'courrier_signature',
    reference: c.numero ?? 'Sans numéro',
    titre: c.objet,
    sousTitre: [c.correspondant_nom, c.service_affectataire?.nom].filter(Boolean).join(' · '),
    enRetard: Boolean(c.en_retard),
    echeance: c.echeance_interne ?? null,
    ordre: ordre(Boolean(c.en_retard), c.echeance_interne ?? null),
  };
}

export const LIBELLES_TYPE: Record<TypeElement, string> = {
  dossier_decision: 'Décision attendue',
  dossier_signature: 'À signer',
  courrier_signature: 'Courrier à signer',
  mandat_a_ordonnancer: 'Mandat à ordonnancer',
};

function depuisLiquidation(l: LiquidationDepense): ElementFile {
  return {
    id: l.id,
    type: 'mandat_a_ordonnancer',
    reference: l.numero,
    titre: `${l.engagement.tiers} · ${l.montant.toLocaleString('fr-FR')} F`,
    sousTitre: `${l.ligne.code} · ${l.ligne.libelle} · facture ${l.facture.numero_fournisseur}`,
    enRetard: false,
    echeance: null,
    ordre: ordre(false, l.liquide.le),
  };
}

/** Charge les trois listes et les fusionne ; une liste refusée (sans droit) est ignorée. */
export function useFileDecisions(actif = true) {
  // Mandats à ordonnancer : seulement pour qui décide sur la dépense (maire, président ou délégué).
  const voitMandats = peut(useSession().contexte, 'depense', 'decider');
  const dossiersDecision = useQuery({
    queryKey: [...CLE_FILE, 'dossiers', 'en_attente_decision'],
    queryFn: ({ signal }) => requete<ListePaginee<Dossier>>('/dossiers', { signal, parametres: { etat: 'en_attente_decision', limite: '200' } }),
    enabled: actif,
    retry: false,
  });
  const dossiersSignature = useQuery({
    queryKey: [...CLE_FILE, 'dossiers', 'en_signature'],
    queryFn: ({ signal }) => requete<ListePaginee<Dossier>>('/dossiers', { signal, parametres: { etat: 'en_signature', limite: '200' } }),
    enabled: actif,
    retry: false,
  });
  const courriers = useQuery({
    queryKey: [...CLE_FILE, 'courriers', 'valide'],
    queryFn: ({ signal }) => requete<ListePaginee<Courrier>>('/courriers', { signal, parametres: { sens: 'depart', etat: 'valide', limite: '200' } }),
    enabled: actif,
    retry: false,
  });
  const mandats = useQuery({
    queryKey: [...CLE_FILE, 'depense', 'a_ordonnancer'],
    queryFn: ({ signal }) => requete<{ elements: LiquidationDepense[] }>('/depense/liquidations', { signal, parametres: { a_ordonnancer: 'oui', limite: '200' } }),
    enabled: actif && voitMandats,
    retry: false,
  });
  const elements = useMemo(() => {
    const liste: ElementFile[] = [
      ...(dossiersDecision.data?.elements ?? []).map((d) => depuisDossier(d, 'dossier_decision')),
      ...(dossiersSignature.data?.elements ?? []).map((d) => depuisDossier(d, 'dossier_signature')),
      ...(courriers.data?.elements ?? []).map(depuisCourrier),
      ...(mandats.data?.elements ?? []).map(depuisLiquidation),
    ];
    return liste.sort((a, b) => a.ordre - b.ordre);
  }, [dossiersDecision.data, dossiersSignature.data, courriers.data, mandats.data]);
  const requetes = voitMandats ? [dossiersDecision, dossiersSignature, courriers, mandats] : [dossiersDecision, dossiersSignature, courriers];
  return {
    elements,
    chargement: requetes.some((r) => r.isLoading),
    erreur: requetes.every((r) => r.isError) ? (requetes[0]?.error ?? null) : null,
    partiel: requetes.some((r) => r.isError) && !requetes.every((r) => r.isError),
    recharger: () => requetes.forEach((r) => void r.refetch()),
  };
}

export const CLES_A_INVALIDER = [['decisions'], ['dossiers'], ['courriers'], ['indicateurs'], ['depense'], ['budget']] as const;
